import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import PageShell from "./PageShell";
import PageHero from "./PageHero";
import Infrastructure from "./Infrastructure";
import RelatedPages from "./RelatedPages";
import PullQuote from "./PullQuote";
import Reveal from "./Reveal";
import { ORIG, campusFacilities, campusLabs, campusZones } from "../mock";

export default function OurCampusPage() {
  const [tab, setTab] = useState("facilities");
  const items = tab === "facilities" ? campusFacilities : campusLabs;

  return (
    <PageShell>
      {({ openEnquire }) => (
        <>
          <PageHero
            eyebrow="Our Campus"
            title="A campus designed to"
            italic="grow, flourish, excel."
            image={`${ORIG}/home-banner.webp`}
            blurb="Twenty acres of perfectly designed learning spaces — light-filled studios, science & innovation labs, libraries, sports complexes and a dedicated cultural hub."
          />

          {/* Intro */}
          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
              <Reveal>
                <div className="eyebrow mb-4">The Most Premium CBSE Campus in Delhi NCR</div>
                <h2 className="h-section text-[var(--ink)]">
                  20 acres of perfectly
                  <span className="serif-italic gold-text"> designed learning spaces.</span>
                </h2>
                <span className="gold-divider mx-auto" />
                <p className="serif text-[16px] md:text-[18px] leading-[1.85] text-[var(--ink)]/80 max-w-[820px] mx-auto">
                  The Signature School invested in the design and construction required to construct this landmark
                  building into a state-of-the-art school facility. The campus opened for students in fall 2016 as the
                  premium CBSE school in Delhi NCR — a safe, spacious environment where students are empowered to
                  nurture their love of learning and thrive on challenges.
                </p>
              </Reveal>
            </div>
          </section>

          {/* Tabbed Facilities / Labs */}
          <section className="bg-[var(--cream)] py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[760px] mx-auto mb-8 md:mb-10">
                  <div className="eyebrow mb-4">Inside the Campus</div>
                  <h2 className="h-section text-[var(--ink)]">
                    Built to bring forth
                    <span className="serif-italic gold-text"> the real world.</span>
                  </h2>
                </div>
              </Reveal>

              {/* Tab switch */}
              <Reveal>
                <div className="flex items-center justify-center gap-2 md:gap-3 mb-10">
                  {[
                    { key: "facilities", label: "Facilities" },
                    { key: "labs", label: "Laboratories" },
                  ].map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      className={`px-5 md:px-7 py-2.5 text-[11.5px] md:text-[12px] tracking-[0.28em] uppercase border transition-colors ${
                        tab === t.key
                          ? "bg-[var(--gold)] text-[var(--navy-deep)] border-[var(--gold)]"
                          : "border-[rgba(184,148,86,0.4)] text-[var(--ink)] hover:bg-white"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </Reveal>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {items.map((f, i) => (
                  <Reveal key={f.title} variant="zoom" delay={(i % 3) * 100}>
                    <article className="light-card overflow-hidden h-full flex flex-col bg-white">
                      <div className="aspect-[4/3] overflow-hidden bg-[var(--navy)]">
                        <img src={f.image} alt={f.title} loading="lazy" className="w-full h-full object-cover img-lift" />
                      </div>
                      <div className="p-6 md:p-7 flex-1">
                        <div className="eyebrow mb-2">0{i + 1}</div>
                        <h3 className="serif text-[22px] md:text-[26px] text-[var(--ink)] leading-tight">
                          {f.title}
                        </h3>
                        <span className="gold-divider-thin block my-4" />
                        <p className="text-[14.5px] leading-[1.75] text-[var(--ink)]/75">{f.body}</p>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Campus Map / Zones */}
          <section className="relative py-14 md:py-20 overflow-hidden" style={{ background: "var(--navy-deep)" }}>
            <div className="mandala-bg" style={{ opacity: 0.3 }} />
            <div className="relative max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[760px] mx-auto mb-10 md:mb-12">
                  <div className="eyebrow mb-4">Campus Map</div>
                  <h2 className="h-section text-white">
                    Seventeen learning zones,
                    <span className="block serif-italic gold-text">one signature campus.</span>
                  </h2>
                  <span className="gold-divider mx-auto" />
                </div>
              </Reveal>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {campusZones.map((z, i) => (
                  <Reveal key={z} delay={(i % 4) * 80}>
                    <div className="flex items-center gap-3 p-4 border border-[rgba(184,148,86,0.25)] bg-[rgba(255,255,255,0.03)] hover:border-[var(--gold)] transition-colors">
                      <span className="serif-italic gold-text text-[20px] md:text-[22px] leading-none">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="serif text-white/85 text-[14px] md:text-[15px] leading-tight">{z}</span>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal>
                <div className="mt-12 md:mt-14 text-center">
                  <button onClick={openEnquire} className="btn-gold inline-flex items-center gap-2" style={{ background: "var(--gold)", color: "var(--navy-deep)" }}>
                    Book a Campus Visit <ArrowUpRight size={16} />
                  </button>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Existing infrastructure tabs */}
          <Infrastructure />

          <PullQuote
            text="Spaces shape the children who learn inside them. Light, air, wood, water — these are the quiet teachers of every great school."
          />
          <RelatedPages current="/our-campus.html" />
        </>
      )}
    </PageShell>
  );
}
